import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "../components/ui/button";

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send to a backend
    console.log("Form submitted:", formData);
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0]">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-[#FF6B00] hover:text-[#E65C00] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-black text-[#1A1C19] mb-4" style={{ fontFamily: "Fraunces, serif" }}>
          Contact Us
        </h1>
        <p className="text-lg text-[#4A5043] mb-10">
          Have a question or need assistance? We're here to help! Reach out to us using any of the methods below.
        </p>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Contact Information */}
          <div>
            <h2 className="text-xl font-bold text-[#1A1C19] mb-6">Get in Touch</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFF5E6] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="text-[#FF6B00]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[#1A1C19] mb-1">Email Support</h3>
                  <p className="text-[#4A5043]">support@citrusburnsupport.com</p>
                  <p className="text-sm text-gray-500">We typically respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFF5E6] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#FF6B00]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[#1A1C19] mb-1">Phone Support</h3>
                  <p className="text-[#4A5043]">1-800-555-0123</p>
                  <p className="text-sm text-gray-500">Mon-Fri: 9AM - 6PM EST</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFF5E6] rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#FF6B00]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[#1A1C19] mb-1">Mailing Address</h3>
                  <p className="text-[#4A5043]">
                    Customer Support Center<br />
                    123 Wellness Drive, Suite 100<br />
                    Miami, FL 33101
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFF5E6] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="text-[#FF6B00]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[#1A1C19] mb-1">Business Hours</h3>
                  <p className="text-[#4A5043]">
                    Monday - Friday: 9:00 AM - 6:00 PM EST<br />
                    Saturday: 10:00 AM - 4:00 PM EST<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Link */}
            <div className="mt-8 p-4 bg-[#E8F5E9] rounded-xl border border-[#C8E6C9]">
              <h3 className="font-bold text-[#2E7D32] mb-2">Looking for quick answers?</h3>
              <p className="text-sm text-[#4A5043] mb-3">
                Check out our FAQ section for answers to commonly asked questions.
              </p>
              <Link to="/#faq" className="text-[#FF6B00] font-medium hover:underline">
                Visit FAQ →
              </Link>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-xl font-bold text-[#1A1C19] mb-6">Send Us a Message</h2>
            
            {submitted ? (
              <div className="bg-[#E8F5E9] border-2 border-[#2E7D32] rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-[#2E7D32] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#2E7D32] mb-2">Message Sent!</h3>
                <p className="text-[#4A5043]">
                  Thank you for contacting us. We've received your message and will get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#1A1C19] mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#1A1C19] mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-[#1A1C19] mb-1">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent outline-none transition-all bg-white"
                  >
                    <option value="">Select a subject</option>
                    <option value="order">Order Inquiry</option>
                    <option value="shipping">Shipping Question</option>
                    <option value="refund">Refund Request</option>
                    <option value="product">Product Question</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#1A1C19] mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent outline-none transition-all resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#FF6B00] to-[#FF8C00] hover:from-[#E65C00] hover:to-[#FF6B00] text-white font-bold rounded-xl py-4 shadow-lg hover:shadow-xl transition-all"
                >
                  Send Message
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1A1C19] text-white py-6 text-center text-sm mt-12">
        <p>© {new Date().getFullYear()} All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Contact;
